/**
 * Service Details Modal Component
 * مكون نافذة تفاصيل الخدمة
 */

import React from 'react';
// Icons will be replaced with SVG

const ServiceDetailsModal = ({ isOpen, onClose, service }) => {
  if (!isOpen || !service) return null;

  const formatWorkingDays = (workingDays) => {
    if (!workingDays || workingDays.length === 0) {
      return 'لم يتم تحديد ساعات العمل';
    }

    const dayNames = {
      'Monday': 'الاثنين',
      'Tuesday': 'الثلاثاء',
      'Wednesday': 'الأربعاء',
      'Thursday': 'الخميس',
      'Friday': 'الجمعة',
      'Saturday': 'السبت',
      'Sunday': 'الأحد'
    };

    return workingDays.map(day => 
      `${dayNames[day.day] || day.day}: ${day.start} - ${day.end}`
    ).join('\n');
  };

  const formatSocialLinks = (socialLinks) => {
    if (!socialLinks || socialLinks.length === 0) {
      return [];
    }

    return socialLinks.map(link => ({
      platform: link.platform,
      url: link.url
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            تفاصيل الخدمة
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Service Header */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {service.service_images && service.service_images.length > 0 ? (
                <img
                  className="h-20 w-20 rounded-lg object-cover"
                  src={`${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://app-api.spinndo.com'}/storage/${service.service_images[0].path}`}
                  alt={service.name}
                />
              ) : (
                <div className="h-20 w-20 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                  <span className="text-2xl font-medium text-gray-500 dark:text-gray-300">
                    {service.name.charAt(0)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                {service.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                {service.description}
              </p>
              <div className="flex items-center mt-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  service.is_approved 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}>
                  {service.is_approved ? 'موافق عليها' : 'قيد المراجعة'}
                </span>
              </div>
            </div>
          </div>

          {/* Service Provider Info */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              معلومات مزود الخدمة
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  الاسم:
                </span>
                <p className="text-gray-900 dark:text-white">
                  {service.service_provider?.first_name_ar} {service.service_provider?.last_name_ar}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  البريد الإلكتروني:
                </span>
                <p className="text-gray-900 dark:text-white">
                  {service.service_provider?.email}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  الهاتف:
                </span>
                <p className="text-gray-900 dark:text-white">
                  {service.service_provider?.phone || 'غير محدد'}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  حالة الحساب:
                </span>
                <p className="text-gray-900 dark:text-white">
                  {service.service_provider?.account_status === 'premium' ? 'مميز' : 'عادي'}
                </p>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                تفاصيل الخدمة
              </h4>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    الفئة:
                  </span>
                  <p className="text-gray-900 dark:text-white">
                    {service.category?.name_ar || service.category?.name}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    المدينة:
                  </span>
                  <p className="text-gray-900 dark:text-white">
                    {service.city?.name}
                  </p>
                </div>
                {service.website && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      الموقع الإلكتروني:
                    </span>
                    <p className="text-gray-900 dark:text-white">
                      <a 
                        href={service.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {service.website}
                      </a>
                    </p>
                  </div>
                )}
                {service.longitude && service.latitude && (
                  <div>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      الموقع:
                    </span>
                    <p className="text-gray-900 dark:text-white">
                      {service.latitude}, {service.longitude}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                ساعات العمل
              </h4>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <pre className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                  {formatWorkingDays(service.working_days)}
                </pre>
              </div>
            </div>
          </div>

          {/* Social Links */}
          {service.social_links && service.social_links.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                روابط التواصل الاجتماعي
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {formatSocialLinks(service.social_links).map((link, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {link.platform}
                        </p>
                        <a 
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                        >
                          {link.url}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Images */}
          {service.service_images && service.service_images.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                صور الخدمة
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {service.service_images.map((image, index) => (
                  <div key={index} className="relative">
                    <img
                      src={`${process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'https://app-api.spinndo.com'}/storage/${image.path}`}
                      alt={`Service image ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Stats */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              إحصائيات الخدمة
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {service.service_images?.length || 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  عدد الصور
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {service.working_days?.length || 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  أيام العمل
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {service.social_links?.length || 0}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  روابط التواصل
                </p>
              </div>
            </div>
          </div>

          {/* Creation Date */}
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
            تم إنشاء الخدمة في: {new Date(service.created_at).toLocaleDateString('en-US')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsModal;
